import type { Meter } from "@/models/meter"
import { ResourceName, resources } from "@/models/resource"

import {
  type IntervalDisplayUnit,
  defaultResource,
  displayUnitForResource,
  intervalDisplayUnit,
  latestIntervalStartDatetime,
  metersForResource,
  toMeterIdsQueryParam,
} from "./interval-utils"
import type { IntervalItem } from "./interval.types"

export interface IntervalResourceCandidate {
  meters: Meter[]
  resource: ResourceName
}

export interface IntervalResourceProbe {
  isError: boolean
  isLoading: boolean
  items: IntervalItem[]
  resource: ResourceName
}

interface IntervalResourceScopeBase {
  options: ResourceName[]
}

export type IntervalResourceScope =
  | (IntervalResourceScopeBase & {
      kind: "error" | "pending" | "unavailable"
    })
  | (IntervalResourceScopeBase & {
      kind: "ready"
      latestStartDatetime: string | null
      meterIds: string
      meters: Meter[]
      resource: ResourceName
      showFlowDirection: boolean
      unit?: IntervalDisplayUnit
    })

interface ToIntervalResourceScopeParams {
  meters: Meter[] | undefined
  metersNotReady: boolean
  probes: IntervalResourceProbe[]
  selectedResource: ResourceName | null
}

export const getResourceCandidates = (
  meters: Meter[] | undefined
): IntervalResourceCandidate[] =>
  resources.flatMap((item) => {
    const resourceMeters = metersForResource(meters, item.name)
    if (resourceMeters.length === 0) {
      return []
    }

    return [{ meters: resourceMeters, resource: item.name }]
  })

const isPreferredDefaultSettled = (
  options: readonly ResourceName[],
  probes: readonly IntervalResourceProbe[]
): boolean =>
  defaultResource(
    options.concat(
      probes.filter((probe) => probe.isLoading).map((probe) => probe.resource)
    )
  ) === defaultResource(options)

const toUnresolvedScope = (
  kind: "error" | "pending" | "unavailable",
  options: ResourceName[]
): IntervalResourceScope => ({
  kind,
  options,
})

const toReadyScope = (
  meters: Meter[] | undefined,
  options: ResourceName[],
  probes: IntervalResourceProbe[],
  resource: ResourceName
): IntervalResourceScope => {
  const selectedProbe = probes.find((probe) => probe.resource === resource)
  const resourceMeters = metersForResource(meters, resource)

  return {
    kind: "ready",
    latestStartDatetime: latestIntervalStartDatetime(
      selectedProbe?.items ?? []
    ),
    meterIds: toMeterIdsQueryParam(resourceMeters),
    meters: resourceMeters,
    options,
    resource,
    showFlowDirection: resource === ResourceName.Electricity,
    unit:
      intervalDisplayUnit(selectedProbe?.items ?? []) ??
      displayUnitForResource(resource),
  }
}

export const toIntervalResourceScope = ({
  meters,
  metersNotReady,
  probes,
  selectedResource,
}: ToIntervalResourceScopeParams): IntervalResourceScope => {
  const hasProbeError = probes.some((probe) => probe.isError)
  const isDiscovering =
    metersNotReady || probes.some((probe) => probe.isLoading)
  const options = probes
    .filter((probe) => !probe.isError && probe.items.length > 0)
    .map((probe) => probe.resource)

  const autoDefault = defaultResource(options)
  const resource =
    selectedResource != null && options.includes(selectedResource)
      ? selectedResource
      : autoDefault

  if (isDiscovering) {
    if (
      resource == null ||
      hasProbeError ||
      (resource === autoDefault && !isPreferredDefaultSettled(options, probes))
    ) {
      return toUnresolvedScope("pending", options)
    }

    return toReadyScope(meters, options, probes, resource)
  }

  if (hasProbeError) {
    return toUnresolvedScope("error", options)
  }

  if (resource == null) {
    return toUnresolvedScope("unavailable", options)
  }

  return toReadyScope(meters, options, probes, resource)
}
